// src/utils/useAuth.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { startNotificationListener, requestNotificationPermission } from "./browserNotifications";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const authValue = useProvideAuth();
  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

function useProvideAuth() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  // -------- AUTH + PROFILE LOADER --------
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      setUser(u);

      if (u) {
        const ref = doc(db, "users", u.uid);
        const unsubProfile = onSnapshot(ref, (snap) => {
          setProfile(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        });

        window.__unsubProfile = unsubProfile;
      } else {
        setProfile(null);
        if (window.__unsubProfile) {
          window.__unsubProfile();
          window.__unsubProfile = null;
        }
      }
    });

    return () => {
      unsubAuth();
      if (window.__unsubProfile) {
        window.__unsubProfile();
        window.__unsubProfile = null;
      }
    };
  }, []);

  // -------- BROWSER NOTIFICATIONS LISTENER --------
  useEffect(() => {
    if (!user || !profile) return;

    // Request notification permission when user logs in
    if (profile.browserNotifications !== false) {
      requestNotificationPermission().then((granted) => {
        if (granted) {
          // Start listening for new notifications
          const unsubNotifications = startNotificationListener(user.uid, profile);
          window.__unsubNotifications = unsubNotifications;
        }
      });
    }

    return () => {
      if (window.__unsubNotifications) {
        window.__unsubNotifications();
        window.__unsubNotifications = null;
      }
    };
  }, [user, profile]);

  // -------- EFFECTIVE ROLE (ADMIN OVERRIDE) --------
  // If user is an admin and has activeRole set, effectiveRole uses activeRole.
  const effectiveRole =
    profile?.role === "admin" ? profile.activeRole || "admin" : profile?.role || null;

  // -------- ROLE SWITCHER (ADMIN ONLY) --------
  async function switchRole(newRole) {
    if (!user || !profile) return;
    // only admins can switch
    if (profile.role !== "admin") return;
    try {
      await updateDoc(doc(db, "users", user.uid), {
        activeRole: newRole,
      });
    } catch (err) {
      console.error("Error switching role:", err);
      throw err;
    }
  }

  // -------- LOGIN / LOGOUT (lazy import the authService) --------
  const login = (email, pw) =>
    import("./authService").then((m) => m.signinWithEmail(email, pw));

  const logout = () =>
  import("./authService").then((m) => m.signout());

  // -------- RETURN CONTEXT --------
  return {
    user,
    profile,
    effectiveRole, // important: use this everywhere for UI/guards
    switchRole,
    login,
    logout,
  };
}
